import type { CSVUploadFormData } from "../types/form-inputs";

import { client } from "../api/client.gen";

// Parse only the headers (first line) of a CSV text
export const parseCSVHeaders = (csvText: string): string[] => {
  const firstLine = csvText.split("\n")[0];
  return firstLine
    .split(",")
    .map((header) => header.trim().replace(/^["']|["']$/g, ""));
};

// Transform form data to API mapping format used by the backend
export const transformToApiMapping = (
  data: CSVUploadFormData,
): {
  columns: Record<string, { entityTypeId?: string; ignore: boolean }>;
  relationships: Array<{
    source: { column: string; entityTypeId: string };
    target: { column: string; entityTypeId: string };
    type: string;
  }>;
} => {
  const columns: Record<string, { entityTypeId?: string; ignore: boolean }> =
    {};

  data.columnsMappings.forEach((mapping) => {
    const ignoreColumn = mapping.ignore === "true";
    columns[mapping.name] = {
      ...(mapping.entityId &&
        !ignoreColumn && { entityTypeId: mapping.entityId }),
      ignore: ignoreColumn,
    };
  });

  const relationships = data.relationships.map((rel) => {
    const sourceMapping = data.columnsMappings.find(
      (m) => m.entityId === rel.fromEntityId,
    );
    const targetMapping = data.columnsMappings.find(
      (m) => m.entityId === rel.toEntityId,
    );

    if (!sourceMapping || !targetMapping) {
      throw new Error(
        "Invalid relationship mapping: source or target not found",
      );
    }

    return {
      source: { column: sourceMapping.name, entityTypeId: rel.fromEntityId },
      target: { column: targetMapping.name, entityTypeId: rel.toEntityId },
      type: rel.name,
    };
  });

  return { columns, relationships };
};

// Perform the multipart upload and return a job id if the upload starts an async job
export const uploadCsvAndGetJobId = async (
  graphId: string,
  file: File,
  mapping: ReturnType<typeof transformToApiMapping>,
): Promise<null | string> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mapping", JSON.stringify(mapping));

  const { auth, baseUrl } = client.getConfig();
  const apiBase = baseUrl || "http://localhost:8080";

  const response = await fetch(`${apiBase}/workspaces/${graphId}/upload`, {
    body: formData,
    headers: {
      ...(auth && { Authorization: `Bearer ${auth}` }),
    },
    method: "POST",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${response.status} ${errorText}`);
  }

  const result = (await response.json()) as { id?: string; jobId?: string };
  return result.jobId || result.id || null;
};
